import type { NavigationTab, Tab } from '~/components/TabsNavigator';

import { computed, onMounted, ref, watch } from 'vue';

export type TabSectionName = 'Fotos' | 'Vídeos' | 'Mapa';
export interface TabConfig {
	id: Tab;
	label: TabSectionName;
	count?: number;
	inflection?: {
		singular: string;
		plural: string;
	};
}

export interface UseTabsOptions {
	tabsConfig: TabConfig[];
	initialTab?: Tab;
	isOpen?: Ref<boolean>;
}

export function useTabs({ tabsConfig, initialTab, isOpen }: UseTabsOptions) {
	const currentTab = ref<Tab>(initialTab || tabsConfig[0]?.id || 'images');

	const isCurrentTab = (tab: Tab) => currentTab.value === tab;

	const tabs = computed<NavigationTab<Tab>[]>(() =>
		tabsConfig.map((tab: TabConfig) => ({
			id: tab.id,
			label:
				tab.count !== undefined && tab.inflection
					? `${tab.count} ${tab.count === 1 ? tab.inflection.singular : tab.inflection.plural}`
					: tab.label,
			active: currentTab.value === tab.id
		}))
	);

	const sectionName = computed(() => {
		const currentTabConfig = tabsConfig.find(
			tab => tab.id === currentTab.value
		);
		return currentTabConfig?.label as TabSectionName;
	});

	const setCurrentTab = (tab: Tab) => {
		currentTab.value = tab;
	};

	onMounted(() => {
		if (isOpen?.value) {
			setCurrentTab(initialTab || 'images');
		}
	});

	watch(
		() => isOpen?.value,
		newValue => {
			if (!newValue) {
				setCurrentTab('images');
			}
		}
	);

	return {
		currentTab,
		isCurrentTab,
		tabs,
		sectionName,
		setCurrentTab
	};
}
